import React from 'react'
import type { TokenModel } from '../../defs/defs'
import appApi, { TokenParams } from '../../classes/appApi'
import generateContextProvider from '../../shared/hooks/GenerateContextProvider'
import EqxError from '../../shared/components/EqxError'
import EqxLoading from '../../shared/components/EqxLoading'
import { GO_API } from '../../classes/appApi'
import { useAuthOAuthContext } from './AppAuthOAuth'
import { useBaseConfigContext } from './AppBaseConfig'
import { useTenantlessDeploymentContext } from './AppTenantlessDeploymentProvider'

/** Tenantless API with in-memory backend JWT exchanged from MSAL token. */
const AppTenantlessAuthedApiWrapper = (props: {
    provider: React.FC<{ value: appApi; children: React.ReactNode }>
    children: React.ReactNode
}) => {
    const ApiProvider = props.provider
    const config = useBaseConfigContext()
    const oauth = useAuthOAuthContext()
    const { environment, instance } = useTenantlessDeploymentContext()

    const api = React.useMemo(
        () =>
            new appApi({
                config: config.api,
                environment,
                instance,
            }),
        [config.api, environment, instance]
    )

    const [backendToken, setBackendToken] = React.useState<string | undefined>()
    const [exchangeError, setExchangeError] = React.useState<string | undefined>()
    const [exchangeInFlight, setExchangeInFlight] = React.useState(false)

    React.useEffect(() => {
        let cancelled = false

        const runExchange = async () => {
            const aadToken = oauth.aadAccessToken
            if (!aadToken) {
                setBackendToken(undefined)
                setExchangeError(undefined)
                return
            }

            setExchangeInFlight(true)
            setExchangeError(undefined)

            try {
                const response = (await api.post(`${GO_API}/token/tenantless`, {
                    aadToken,
                    version: config.version,
                } as TokenParams)) as TokenModel
                if (cancelled) return
                setBackendToken(response.accessToken)
            } catch (error) {
                console.error('Failed to exchange tenantless token:', error)
                if (cancelled) return
                setBackendToken(undefined)
                setExchangeError(
                    'Sorry, an error occurred while trying to log in to Continuum, please contact support.'
                )
            } finally {
                if (!cancelled) setExchangeInFlight(false)
            }
        }

        void runExchange()
        return () => {
            cancelled = true
        }
    }, [oauth.aadAccessToken, api, config.version])

    // Keep token only in memory; attach to tenantless API instance for request auth.
    api.token = backendToken

    // Prevent descendants from firing tenantless API calls before exchange completes.
    const awaitingTenantlessJwt =
        !!oauth.aadAccessToken && !backendToken && exchangeError === undefined

    if (exchangeInFlight || awaitingTenantlessJwt) {
        return <EqxLoading message="Logging in..." />
    }

    if (exchangeError && !backendToken) {
        return <EqxError error={exchangeError} />
    }

    return <ApiProvider value={api}>{props.children}</ApiProvider>
}

const {
    ContextProvider: AppTenantlessAuthedApiProvider,
    useContext: useTenantlessAuthedApiContext,
    useContextOptional: useTenantlessAuthedApiContextOptional,
} = generateContextProvider<appApi>(undefined, AppTenantlessAuthedApiWrapper)

export {
    AppTenantlessAuthedApiProvider,
    useTenantlessAuthedApiContext,
    useTenantlessAuthedApiContextOptional,
}
